// Heart Games Progress Service
// Handles progress tracking and achievement management

import { GameProgress, GameResult, Achievement } from '../../types/heartGames';
import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';

const API_BASE = `${config.api.baseUrl}/heart-games`;

function headers(): HeadersInit {
  return { Authorization: `Bearer ${authInterceptor.getAccessToken() || ''}` };
}

async function unwrap(response: Response): Promise<any> {
  if (!response.ok) throw new Error(`Request failed: ${response.statusText}`);
  const body = await response.json();
  return body.data;
}

function toGameProgress(row: any, userId: string): GameProgress {
  return {
    userId,
    gamesPlayed: row.games_played ?? 0,
    totalScore: row.total_score ?? { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0, type: 'UNKNOWN', confidence: 0, traits: [] },
    gameHistory: [],
    achievements: [],
    streak: row.streak ?? 0,
    lastPlayed: row.last_played ? new Date(row.last_played) : new Date(),
  };
}

export class ProgressService {
  // Mock achievements for development
  private static readonly ACHIEVEMENTS: Achievement[] = [
    {
      id: 'first-game',
      name: 'First Steps',
      description: 'Complete your first Heart Game',
      icon: '🎮',
      unlockedAt: new Date(),
      category: 'completion'
    },
    {
      id: 'streak-7',
      name: 'Week Warrior',
      description: 'Play Heart Games for 7 consecutive days',
      icon: '🔥',
      unlockedAt: new Date(),
      category: 'streak'
    },
    {
      id: 'all-games',
      name: 'Heart Master',
      description: 'Complete all available Heart Games',
      icon: '👑',
      unlockedAt: new Date(),
      category: 'completion'
    },
    {
      id: 'quick-thinker',
      name: 'Quick Thinker',
      description: 'Complete a quick-fire game in under 2 minutes',
      icon: '⚡',
      unlockedAt: new Date(),
      category: 'accuracy'
    },
    {
      id: 'social-butterfly',
      name: 'Social Butterfly',
      description: 'Complete 5 social games',
      icon: '🦋',
      unlockedAt: new Date(),
      category: 'social'
    }
  ];

  private static mockProgress: GameProgress[] = [];

  /**
   * Get user's game progress
   */
  static async getUserProgress(userId: string): Promise<GameProgress | null> {
    try {
      const response = await fetch(`${API_BASE}/progress`, { headers: headers() });
      const data = await unwrap(response);
      return toGameProgress(data, userId);
    } catch (error) {
      console.error('Error fetching user progress, falling back to local progress:', error);
    }

    try {
      const progress = this.mockProgress.find(p => p.userId === userId);
      if (progress) {
        return { ...progress };
      }

      // Create new progress for user
      const newProgress: GameProgress = {
        userId,
        gamesPlayed: 0,
        totalScore: {
          E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0,
          type: 'UNKNOWN',
          confidence: 0,
          traits: []
        },
        gameHistory: [],
        achievements: [],
        streak: 0,
        lastPlayed: new Date()
      };

      this.mockProgress.push(newProgress);
      return { ...newProgress };
    } catch (error) {
      console.error('Error fetching user progress:', error);
      throw new Error('Failed to fetch user progress');
    }
  }

  /**
   * Update progress after completing a game
   */
  static async updateProgress(userId: string, gameResult: GameResult): Promise<GameProgress> {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      let progress = this.mockProgress.find(p => p.userId === userId);
      
      if (!progress) {
        progress = {
          userId,
          gamesPlayed: 0,
          totalScore: {
            E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0,
            type: 'UNKNOWN',
            confidence: 0,
            traits: []
          },
          gameHistory: [],
          achievements: [],
          streak: 0,
          lastPlayed: new Date()
        };
        this.mockProgress.push(progress);
      }

      // Update progress
      progress.gamesPlayed += 1;
      progress.gameHistory.push(gameResult);
      progress.lastPlayed = new Date();

      // Update streak
      const daysSinceLastPlay = Math.floor(
        (Date.now() - progress.lastPlayed.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      if (daysSinceLastPlay <= 1) {
        progress.streak += 1;
      } else {
        progress.streak = 1;
      }

      // Update total score (average of all games)
      this.updateTotalScore(progress);

      // Check for new achievements
      await this.checkAchievements(progress);

      return { ...progress };
    } catch (error) {
      console.error('Error updating progress:', error);
      throw new Error('Failed to update progress');
    }
  }

  /**
   * Get user's achievements
   */
  static async getUserAchievements(userId: string): Promise<Achievement[]> {
    try {
      const response = await fetch(`${API_BASE}/achievements`, { headers: headers() });
      const data = await unwrap(response);
      return (data ?? []).map((row: any) => ({
        id: row.code, name: row.name, description: row.description, icon: row.icon,
        unlockedAt: new Date(row.unlockedAt), category: row.category,
      }));
    } catch (error) {
      console.error('Error fetching achievements, falling back to local progress:', error);
      const progress = this.mockProgress.find(p => p.userId === userId);
      return progress ? [...progress.achievements] : [];
    }
  }

  /**
   * Unlock a specific achievement
   */
  static async unlockAchievement(userId: string, achievementId: string): Promise<Achievement | null> {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const progress = this.mockProgress.find(p => p.userId === userId);
      if (!progress) {
        throw new Error('User progress not found');
      }

      const achievement = this.ACHIEVEMENTS.find(a => a.id === achievementId);
      if (!achievement) {
        throw new Error('Achievement not found');
      }

      // Check if already unlocked
      const alreadyUnlocked = progress.achievements.some(a => a.id === achievementId);
      if (alreadyUnlocked) {
        return null;
      }

      // Unlock achievement
      const unlockedAchievement: Achievement = {
        ...achievement,
        unlockedAt: new Date()
      };

      progress.achievements.push(unlockedAchievement);
      return { ...unlockedAchievement };
    } catch (error) {
      console.error('Error unlocking achievement:', error);
      throw new Error('Failed to unlock achievement');
    }
  }

  /**
   * Get leaderboard data
   */
  static async getLeaderboard(limit: number = 10): Promise<{ userId: string; gamesPlayed: number; streak: number }[]> {
    try {
      const response = await fetch(`${API_BASE}/leaderboard?limit=${limit}`, { headers: headers() });
      const data = await unwrap(response);
      return (data ?? []).map((row: any) => ({ userId: row.userId, gamesPlayed: row.gamesPlayed, streak: row.streak }));
    } catch (error) {
      console.error('Error fetching leaderboard, falling back to local progress:', error);
      return this.mockProgress
        .sort((a, b) => b.gamesPlayed - a.gamesPlayed)
        .slice(0, limit)
        .map(progress => ({
          userId: progress.userId,
          gamesPlayed: progress.gamesPlayed,
          streak: progress.streak
        }));
    }
  }

  /**
   * Get progress statistics
   */
  static async getProgressStats(userId: string): Promise<{
    totalGames: number;
    averageScore: number;
    longestStreak: number;
    currentStreak: number;
    achievementsUnlocked: number;
    favoriteGameType: string;
  }> {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const progress = this.mockProgress.find(p => p.userId === userId);
      if (!progress) {
        return {
          totalGames: 0,
          averageScore: 0,
          longestStreak: 0,
          currentStreak: 0,
          achievementsUnlocked: 0,
          favoriteGameType: 'None'
        };
      }

      // Calculate statistics
      const totalGames = progress.gamesPlayed;
      const averageScore = progress.gameHistory.length > 0 
        ? progress.gameHistory.reduce((sum, game) => sum + game.score.confidence, 0) / progress.gameHistory.length
        : 0;
      
      const longestStreak = Math.max(progress.streak, 0);
      const currentStreak = progress.streak;
      const achievementsUnlocked = progress.achievements.length;
      
      // Determine favorite game type
      const gameTypeCounts: { [key: string]: number } = {};
      progress.gameHistory.forEach(game => {
        gameTypeCounts[game.gameId] = (gameTypeCounts[game.gameId] || 0) + 1;
      });
      
      const favoriteGameType = Object.keys(gameTypeCounts).length > 0
        ? Object.entries(gameTypeCounts).sort(([,a], [,b]) => b - a)[0][0]
        : 'None';

      return {
        totalGames,
        averageScore: Math.round(averageScore),
        longestStreak,
        currentStreak,
        achievementsUnlocked,
        favoriteGameType
      };
    } catch (error) {
      console.error('Error fetching progress stats:', error);
      throw new Error('Failed to fetch progress stats');
    }
  }

  /**
   * Reset user progress (for testing)
   */
  static async resetProgress(userId: string): Promise<void> {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const index = this.mockProgress.findIndex(p => p.userId === userId);
      if (index !== -1) {
        this.mockProgress.splice(index, 1);
      }
    } catch (error) {
      console.error('Error resetting progress:', error);
      throw new Error('Failed to reset progress');
    }
  }

  // Private helper methods
  private static updateTotalScore(progress: GameProgress): void {
    if (progress.gameHistory.length === 0) return;

    // Calculate average scores across all games
    const totalScores = progress.gameHistory.reduce((totals, game) => ({
      E: totals.E + game.score.E,
      I: totals.I + game.score.I,
      S: totals.S + game.score.S,
      N: totals.N + game.score.N,
      T: totals.T + game.score.T,
      F: totals.F + game.score.F,
      J: totals.J + game.score.J,
      P: totals.P + game.score.P
    }), { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 });

    const gameCount = progress.gameHistory.length;
    progress.totalScore = {
      E: totalScores.E / gameCount,
      I: totalScores.I / gameCount,
      S: totalScores.S / gameCount,
      N: totalScores.N / gameCount,
      T: totalScores.T / gameCount,
      F: totalScores.F / gameCount,
      J: totalScores.J / gameCount,
      P: totalScores.P / gameCount,
      type: progress.gameHistory[progress.gameHistory.length - 1].score.type,
      confidence: progress.gameHistory.reduce((sum, game) => sum + game.score.confidence, 0) / gameCount,
      traits: progress.gameHistory[progress.gameHistory.length - 1].score.traits
    };
  }

  private static async checkAchievements(progress: GameProgress): Promise<void> {
    // Check for first game achievement
    if (progress.gamesPlayed === 1) {
      await this.unlockAchievement(progress.userId, 'first-game');
    }

    // Check for streak achievements
    if (progress.streak === 7) {
      await this.unlockAchievement(progress.userId, 'streak-7');
    }

    // Check for all games achievement (assuming 6 games total)
    if (progress.gamesPlayed >= 6) {
      await this.unlockAchievement(progress.userId, 'all-games');
    }

    // Check for quick thinker achievement
    const quickGames = progress.gameHistory.filter(game => 
      game.duration < 120 && game.gameId.includes('sparks')
    );
    if (quickGames.length > 0) {
      await this.unlockAchievement(progress.userId, 'quick-thinker');
    }

    // Check for social butterfly achievement
    const socialGames = progress.gameHistory.filter(game => 
      game.gameId.includes('sync')
    );
    if (socialGames.length >= 5) {
      await this.unlockAchievement(progress.userId, 'social-butterfly');
    }
  }
}









